/**
 * C3 — Copilot adoption source data (Reports.Read.All).
 *
 * Uses the Microsoft 365 Copilot usage *user detail* report. This is a beta
 * endpoint and is treated as BEST-EFFORT (§3.4): no preview API is load-bearing.
 * We request JSON explicitly; the caller degrades gracefully on 403/consent.
 *
 * Per-app "last activity date" fields are date strings when the user has used
 * that app's Copilot in the period, or empty/absent otherwise — that is what the
 * adoption engine reasons over.
 */
import { graphGet } from '@/graph/client';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import type { GraphCollection } from '@/graph/types';

export type UsagePeriod = 'D7' | 'D30' | 'D90' | 'D180';

/** Subset of the Copilot usage user-detail row we depend on. */
export interface CopilotUsageRow {
  reportRefreshDate?: string | null;
  userPrincipalName?: string | null;
  displayName?: string | null;
  lastActivityDate?: string | null;
  copilotChatLastActivityDate?: string | null;
  microsoftTeamsCopilotLastActivityDate?: string | null;
  wordCopilotLastActivityDate?: string | null;
  excelCopilotLastActivityDate?: string | null;
  powerPointCopilotLastActivityDate?: string | null;
  outlookCopilotLastActivityDate?: string | null;
  oneNoteCopilotLastActivityDate?: string | null;
  loopCopilotLastActivityDate?: string | null;
}

/**
 * GET the Copilot usage user-detail report as JSON.
 * @param period reporting window (default 30 days).
 */
export async function getCopilotUsageUserDetail(
  period: UsagePeriod = 'D30',
): Promise<CopilotUsageRow[]> {
  const res = await graphGet<GraphCollection<CopilotUsageRow> | CopilotUsageRow[]>(
    `/reports/getMicrosoft365CopilotUsageUserDetail(period='${period}')`,
    {
      scopes: CAPABILITY_SCOPES.C3.scopes,
      version: 'beta',
      query: { $format: 'application/json' },
      bestEffort: true,
    },
  );
  // The report may return a bare array or a {value:[]} envelope depending on shape.
  if (Array.isArray(res)) return res;
  return res.value ?? [];
}
